
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { getAssessmentBySlug } from '@/services/assessmentService';
import { loadPremiumProgress, deletePremiumProgress } from '@/services/premiumProgressService';
import { Assessment } from '@/types/assessment';
import { useToast } from '@/hooks/use-toast';
import Logo from '@/icons/Logo';
import PremiumDashboard from '@/components/premium-dashboard/PremiumDashboard';
import PremiumResumeDialog from '@/components/premium-dashboard/PremiumResumeDialog';
import PremiumAssessment from '@/pages/assessment/PremiumAssessment';

const hasOngoingAssessment = (assessmentId: string): boolean => {
  try {
    const stored = localStorage.getItem(`assessment_${assessmentId}_responses`);
    if (!stored) return false;

    const storedProgress = localStorage.getItem(`assessment-progress-${assessmentId}`);
    if (storedProgress) {
      const parsedProgress = JSON.parse(storedProgress);
      if (parsedProgress?.step === 'complete') return false;
    }

    const parsed = JSON.parse(stored);
    return Object.keys(parsed).length > 0;
  } catch {
    return false;
  }
};

const hasCompletedPremiumResponse = async (assessmentId: string): Promise<boolean> => {
  try {
    const { data } = await supabase.functions.invoke('get-my-responses');
    if (!Array.isArray(data)) return false;

    return data.some((response: any) => response.assessmentId === assessmentId && Boolean(response.userId));
  } catch {
    return false;
  }
};

const clearAssessmentProgress = (assessmentId: string) => {
  localStorage.removeItem(`assessment_${assessmentId}_responses`);
  localStorage.removeItem(`assessment-progress-${assessmentId}`);
  localStorage.removeItem(`assessment-progress-premium-${assessmentId}`);
  deletePremiumProgress(assessmentId).catch(() => {});
};

const PremiumPortal = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [premiumView, setPremiumView] = useState<'dashboard' | 'assessment'>('dashboard');
  const [selectedAssessment, setSelectedAssessment] = useState<Assessment | null>(null);
  const [loadingSelected, setLoadingSelected] = useState(false);
  const [startFresh, setStartFresh] = useState(false);
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false);
  const [pendingAssessment, setPendingAssessment] = useState<Assessment | null>(null);
  const { toast } = useToast();

  // Auth check
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        navigate('/', { replace: true });
        return;
      }

      const { data: orgMember } = await supabase
        .from('organization_members')
        .select('id')
        .eq('id', session.user.id)
        .maybeSingle();

      if (!orgMember) {
        navigate('/', { replace: true });
        return;
      }

      setLoading(false);
    };

    checkAuth();
  }, [navigate]);

  // Handle browser back button
  useEffect(() => {
    const handlePopState = () => {
      if (premiumView === 'assessment') {
        setPremiumView('dashboard');
        setSelectedAssessment(null);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [premiumView]);

  const launchAssessment = (data: Assessment, fresh: boolean) => {
    if (fresh) {
      clearAssessmentProgress(data.id);
    }
    setStartFresh(fresh);
    setSelectedAssessment(data);
    setPremiumView('assessment');
    window.history.pushState({ view: 'assessment' }, '');
  };

  const handleStartPremiumAssessment = async (assessmentId: string, assessmentSlug: string) => {
    let hasProgress = hasOngoingAssessment(assessmentId);
    const hasCompleted = await hasCompletedPremiumResponse(assessmentId);

    if (hasCompleted) {
      hasProgress = false;
      deletePremiumProgress(assessmentId).catch(() => {});
    }

    if (!hasProgress) {
      try {
        const dbProgress = await loadPremiumProgress(assessmentId);
        if (!hasCompleted && dbProgress && dbProgress.responses && Object.keys(dbProgress.responses).length > 0) {
          hasProgress = true;
          localStorage.setItem(`assessment_${assessmentId}_responses`, JSON.stringify(dbProgress.responses));
        }
      } catch (err) {
        console.warn('Could not check DB progress:', err);
      }
    }

    if (hasProgress) {
      try {
        const data = await getAssessmentBySlug(assessmentSlug);
        if (data) {
          setPendingAssessment(data);
          setResumeDialogOpen(true);
        } else {
          toast({ variant: "destructive", title: "Error", description: "Could not load the selected assessment." });
        }
      } catch {
        toast({ variant: "destructive", title: "Error", description: "Failed to load assessment." });
      }
      return;
    }

    setLoadingSelected(true);
    try {
      const data = await getAssessmentBySlug(assessmentSlug);
      if (data) {
        launchAssessment(data, true);
      } else {
        toast({ variant: "destructive", title: "Error", description: "Could not load the selected assessment." });
      }
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Failed to load assessment." });
    } finally {
      setLoadingSelected(false);
    }
  };

  const handleViewResults = async (assessmentId: string, assessmentSlug: string) => {
    setLoadingSelected(true);
    try {
      const data = await getAssessmentBySlug(assessmentSlug);
      if (data) {
        // Force progress to 'complete' so the results page renders
        const progressKey = `assessment-progress-${assessmentId}`;
        const existing = localStorage.getItem(progressKey);
        const parsed = existing ? JSON.parse(existing) : {};
        localStorage.setItem(progressKey, JSON.stringify({ ...parsed, step: 'complete' }));
        setStartFresh(false);
        setSelectedAssessment(data);
        setPremiumView('assessment');
        window.history.pushState({ view: 'assessment' }, '');
      } else {
        toast({ variant: "destructive", title: "Error", description: "Could not load the assessment." });
      }
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Failed to load assessment." });
    } finally {
      setLoadingSelected(false);
    }
  };

  const handleContinueAssessment = async (assessmentId: string, assessmentSlug: string) => {
    const localStored = localStorage.getItem(`assessment_${assessmentId}_responses`);
    if (!localStored || Object.keys(JSON.parse(localStored || '{}')).length === 0) {
      try {
        const dbProgress = await loadPremiumProgress(assessmentId);
        if (dbProgress?.responses && Object.keys(dbProgress.responses).length > 0) {
          localStorage.setItem(`assessment_${assessmentId}_responses`, JSON.stringify(dbProgress.responses));
        }
      } catch {}
    }

    setLoadingSelected(true);
    try {
      const data = await getAssessmentBySlug(assessmentSlug);
      if (data) {
        launchAssessment(data, false);
      } else {
        toast({ variant: "destructive", title: "Error", description: "Could not load the selected assessment." });
      }
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Failed to load assessment." });
    } finally {
      setLoadingSelected(false);
    }
  };

  const handleResumeChoice = (resume: boolean) => {
    setResumeDialogOpen(false);
    if (pendingAssessment) {
      launchAssessment(pendingAssessment, !resume);
      setPendingAssessment(null);
    }
  };

  const handleBackToDashboard = () => {
    setPremiumView('dashboard');
    setSelectedAssessment(null);
    window.history.back();
  };

  const handleLogout = () => {
    supabase.auth.signOut();
    navigate('/', { replace: true });
  };

  if (loading || loadingSelected) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-muted/30">
        <div className="flex items-center gap-3">
          <Logo className="h-8 w-8" />
          <span className="text-lg text-foreground">Loading...</span>
        </div>
      </div>
    );
  }

  if (premiumView === 'assessment' && selectedAssessment) {
    return (
      <PremiumAssessment
        assessment={selectedAssessment}
        onBack={handleBackToDashboard}
        startFresh={startFresh}
      />
    );
  }

  return (
    <>
      <PremiumDashboard
        onStartAssessment={handleStartPremiumAssessment}
        onContinueAssessment={handleContinueAssessment}
        onViewResults={handleViewResults}
        onLogout={handleLogout}
      />
      <PremiumResumeDialog
        open={resumeDialogOpen}
        onOpenChange={setResumeDialogOpen}
        assessmentTitle={pendingAssessment?.title || ''}
        onResume={() => handleResumeChoice(true)}
        onStartNew={() => handleResumeChoice(false)}
      />
    </>
  );
};

export default PremiumPortal;
